import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, User, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const UserLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { userLoginByUsername } = useAuth();
  const { t } = useLanguage();

  const userLoginText = t('userLogin');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await userLoginByUsername(username, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="text-2xl font-bold text-white tracking-tight">
            AI<span className="text-teal-400">CONNEQTORS</span>
          </a>
          <p className="text-gray-500 mt-2">{userLoginText?.subtitle || 'User Portal'}</p>
        </div>

        {/* Login Card */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-white mb-2">{userLoginText?.title || 'Welcome Back'}</h2>
          <p className="text-gray-500 text-sm mb-6">{t('auth.signInWithCredentials') || 'Sign in with your username and password'}</p>
          
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-gray-300 text-sm mb-2">{userLoginText?.username || 'Username'}</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-12 pr-4 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400 transition-colors"
                  placeholder={userLoginText?.username || 'Enter your username'}
                  required
                  autoComplete="username"
                  data-testid="user-login-username"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-300 text-sm mb-2">{userLoginText?.password || 'Password'}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-12 pr-12 py-3 text-white placeholder-gray-500 outline-none focus:border-teal-400 transition-colors"
                  placeholder={userLoginText?.password || 'Enter your password'}
                  required
                  autoComplete="current-password"
                  data-testid="user-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="user-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {userLoginText?.signingIn || 'Signing in...'}
                </>
              ) : (
                userLoginText?.signIn || 'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-800 text-center">
            <p className="text-gray-500 text-sm">
              {t('auth.credentialsFromAdmin') || 'Login credentials are provided by your administrator.'}
            </p>
            <p className="text-gray-600 text-xs mt-2">
              {userLoginText?.contactAdmin || 'Contact them if you forgot your username or password.'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-gray-600 text-xs text-center mt-6">
          © 2024 AIConneqtors. {t('common.allRightsReserved') || 'All rights reserved.'}
        </p>
      </div>
    </div>
  );
};

export default UserLogin;
